import geohash from 'ngeohash'
import type { MapAssetObject, MapAssetsArray } from '~/types/map-assets.types'

/**
 * Field names the app currently knows how to consume from the map-features API.
 * The API response includes a `headers` array describing the name and order
 * of each field in the flat `assets` array, so we resolve field positions
 * dynamically instead of assuming a fixed layout.
 */
const REQUIRED_MAP_FEATURE_FIELDS = ['id', 'position', 'assetTypeId', 'iconId', 'colorId', 'name'] as const

const OPTIONAL_ALERT_LEVEL_FIELD = 'alertLevel' as const

type MapFeatureField = (typeof REQUIRED_MAP_FEATURE_FIELDS)[number]

export type MapFeatureHeaderIndex = Record<MapFeatureField, number>

/**
 * Build a map of field name -> index within a single record, based on the
 * `headers` array returned by the map-features API.
 * Throws if a required field is missing from the headers.
 */
export const buildMapFeatureHeaderIndex = (headers: string[]): MapFeatureHeaderIndex => {
  const index = {} as MapFeatureHeaderIndex

  for (const field of REQUIRED_MAP_FEATURE_FIELDS) {
    const fieldIndex = headers.indexOf(field)

    if (fieldIndex === -1) {
      throw new Error(`Missing required map feature header: "${field}"`)
    }

    index[field] = fieldIndex
  }

  return index
}

const parseAlertLevel = (rawValue: string | number | null | undefined): number | null => {
  if (rawValue === null || rawValue === undefined) {
    return null
  }
  return Number(rawValue)
}

const parseSiteId = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const siteId = Number(value)
  return Number.isFinite(siteId) ? siteId : null
}

/**
 * Parse the flat `assets` array from the map-features API into MapAssetObject[],
 * using `headers` to resolve each field's position instead of assuming a fixed layout.
 */
export const parseMapFeaturesArray = (assets: MapAssetsArray, headers: string[]): MapAssetObject[] => {
  const fieldIndex = buildMapFeatureHeaderIndex(headers)
  const alertLevelFieldIndex = headers.indexOf(OPTIONAL_ALERT_LEVEL_FIELD)
  const siteIdIndex = headers.indexOf('siteId')
  const stride = headers.length
  const items: MapAssetObject[] = []

  for (let i = 0; i < assets.length; i += stride) {
    const hash = String(assets[i + fieldIndex.position])
    const { latitude, longitude } = geohash.decode(hash)

    items.push({
      id: Number(assets[i + fieldIndex.id]),
      geoHash: hash,
      lng: longitude,
      lat: latitude,
      assetTypeId: Number(assets[i + fieldIndex.assetTypeId]),
      iconId: Number(assets[i + fieldIndex.iconId]),
      colorId: Number(assets[i + fieldIndex.colorId]),
      name: String(assets[i + fieldIndex.name] || ''),
      alertLevel: alertLevelFieldIndex === -1 ? null : parseAlertLevel(assets[i + alertLevelFieldIndex]),
      siteId: siteIdIndex === -1 ? null : parseSiteId(assets[i + siteIdIndex])
    })
  }

  return items
}
